import { useEffect, useState } from "react";
import { api, Manifest } from "../api";

// FIRM-WIDE intelligence: the same de-biased macro read, cross-asset tape and
// eleven-analyst sector desk that feed the board, every book's weekly strike
// sessions, and the Bull Sheet. One page, so "what are the signals saying" has
// one answer.
type MacroBrief = {
  headline_count: number;
  rates?: string;
  cross_asset?: Record<string, { symbol: string; close: number; chg_pct: number }>;
  brief: { regime?: string; cross_asset_note?: string; corroborated?: string[]; watch?: string[] };
};
type SectorRead = {
  name: string; etf: string;
  direction: "bullish" | "bearish" | "neutral";
  conviction: "high" | "medium" | "low";
  regime: string; corroborated: string[]; watch: string[];
  key_names: string[]; sources: number;
};
type SectorBriefs = { sectors: Record<string, SectorRead> };

const SECTOR_TONE: Record<string, string> = {
  bullish: "border-up/40 bg-up/5",
  bearish: "border-down/40 bg-down/5",
  neutral: "border-edge",
};
const DIR_ARROW: Record<string, string> = { bullish: "▲", bearish: "▼", neutral: "—" };
const DIR_TEXT: Record<string, string> = { bullish: "text-up", bearish: "text-down", neutral: "text-slate-500" };
const CONV_TEXT: Record<string, string> = { high: "text-slate-300", medium: "text-slate-500", low: "text-slate-600" };
function sectorRank(s: { direction: string; conviction: string }): number {
  const dir = s.direction === "neutral" ? 10 : 0;
  const conv = { high: 0, medium: 1, low: 2 }[s.conviction] ?? 3;
  return dir + conv;
}

export default function Signals(_: { manifest: Manifest }) {
  const [macro, setMacro] = useState<MacroBrief | null>(null);
  const [sectors, setSectors] = useState<SectorBriefs | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<MacroBrief>("/api/board/macro").then(setMacro).catch((e) => setErr((e as Error).message));
    api.get<SectorBriefs>("/api/board/sectors").then(setSectors).catch(() => {});
    const iv = setInterval(() => {
      api.get<MacroBrief>("/api/board/macro").then(setMacro).catch(() => {});
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const tape = Object.entries(macro?.cross_asset ?? {});

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h1 className="text-lg font-bold">Signals</h1>
        <span className="text-xs text-slate-500">
          the reads that feed the board, every book&rsquo;s strike sessions, and the Bull Sheet
        </span>
      </div>
      {err && <div className="text-down text-sm">{err}</div>}

      {/* Cross-asset tape — rates, dollar, commodities, vol, countries, crypto */}
      {tape.length > 0 && (
        <div className="card">
          <h2 className="label">Cross-asset tape (1-day)</h2>
          <div className="flex flex-wrap gap-1.5">
            {tape.map(([k, v]) => (
              <span key={k} className={`px-2 py-1 rounded-lg border border-edge text-xs font-mono ${
                v.chg_pct >= 0 ? "text-up" : "text-down"}`} title={`${k} — close ${v.close}`}>
                <span className="text-slate-400">{k.replace(/\s*\(.*\)/, "")}</span>{" "}
                {v.chg_pct >= 0 ? "+" : ""}{v.chg_pct.toFixed(2)}%
                {/* Close rendered inline, not tooltip-only — a hover title
                    doesn't exist on touch (Kaizen D3/F4). */}
                <span className="block text-[10px] text-slate-500 leading-tight">{v.close}</span>
              </span>
            ))}
          </div>
          {macro?.rates && <div className="text-xs text-slate-500 font-mono mt-2">{macro.rates}</div>}
        </div>
      )}

      {/* MACRO & NEWS (de-biased, triangulated) */}
      {macro?.brief?.regime && (
        <div className="card space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="label mb-0">Macro &amp; news</h2>
            <span className="text-xs text-slate-500">{macro.headline_count} headlines, cross-checked · framing treated as noise</span>
          </div>
          <p className="text-sm text-slate-200">{macro.brief.regime}</p>
          {macro.brief.cross_asset_note && (
            <p className="text-xs text-slate-400">{macro.brief.cross_asset_note}</p>
          )}
          <div className="grid lg:grid-cols-2 gap-3 pt-1">
            <div>
              <div className="text-xs text-up font-semibold mb-1">Corroborated (signal)</div>
              <ul className="text-xs text-slate-300 space-y-1 list-disc pl-4">
                {(macro.brief.corroborated ?? []).slice(0, 6).map((c, i) => <li key={i}>{c}</li>)}
                {(macro.brief.corroborated ?? []).length === 0 && <li className="text-slate-600 list-none">nothing confirmed</li>}
              </ul>
            </div>
            <div>
              <div className="text-xs text-gold font-semibold mb-1">Watch (unconfirmed / single-source)</div>
              <ul className="text-xs text-slate-400 space-y-1 list-disc pl-4">
                {(macro.brief.watch ?? []).slice(0, 6).map((w, i) => <li key={i}>{w}</li>)}
                {(macro.brief.watch ?? []).length === 0 && <li className="text-slate-600 list-none">—</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* SECTOR DESK — eleven independent Fortune-500 analysts */}
      {sectors?.sectors && Object.keys(sectors.sectors).length > 0 && (
        <div className="card space-y-2">
          <div className="flex items-center gap-2">
            <h2 className="label mb-0">Sector desk</h2>
            <span className="text-xs text-slate-500">
              11 independent analysts · ETF tape + specialist trade-press + global feed, corroborated
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(sectors.sectors)
              .sort((a, b) => sectorRank(a[1]) - sectorRank(b[1]))
              .map(([key, s]) => (
                <div key={key} className={`rounded-lg border p-2 ${SECTOR_TONE[s.direction] ?? "border-edge"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-200">{s.name}</span>
                    <span className={`text-sm ${DIR_TEXT[s.direction] ?? "text-slate-400"}`}>
                      {DIR_ARROW[s.direction] ?? "—"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-mono">
                    <span>{s.etf}</span>
                    <span className={CONV_TEXT[s.conviction] ?? "text-slate-600"}>{s.conviction}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1 line-clamp-3" title={s.regime}>{s.regime}</p>
                  {s.key_names?.length > 0 && (
                    <p className="text-[10px] text-slate-500 mt-1 truncate" title={s.key_names.join("; ")}>
                      {s.key_names.slice(0, 2).join(" · ")}
                    </p>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
